/** A table's playable area, in tiles: how wide along the anchor's x axis, how deep along its y axis. */
export interface BoardSize {
  width: number;
  depth: number;
}

/** The island a page is built on unless it answers « intérieur ». */
export const EXTERIOR_BOARD: BoardSize = { width: 24, depth: 24 };

/**
 * The room. Smaller on purpose: a room is a corner of a story, not a landscape.
 * The asset menu reads this too, to say which assets fit on the table at all.
 */
export const INTERIOR_BOARD: BoardSize = { width: 12, depth: 9 };

/**
 * Where a table of that size sits on the 96x96 grid: the placeable rectangle the
 * board hands the camera and the drop tests.
 */
export interface PlaceableArea {
  placeableWidth: number;
  placeableHeight: number;
  placeableX: number;
  placeableY: number;
}

/**
 * The part of the grid an object may stand on. It is the table the student
 * builds on, so it has to be the table they can see: outdoors that is the
 * wide one, indoors the smaller twelve by nine. Get this wrong and an object
 * dropped past the table's edge sits in mid air without the tile ever
 * turning red, since the red is what falling outside this rectangle draws.
 */
export function placeableAreaFor(isInterior: boolean): PlaceableArea {
  if (!isInterior) {
    return {
      placeableWidth: EXTERIOR_BOARD.width,
      placeableHeight: EXTERIOR_BOARD.depth,
      placeableX: 28,
      placeableY: 28,
    };
  }
  // Higher up the grid than the outdoor board, and that is the whole trick.
  // The camera works out where to look from the extent of the placeable
  // area, which OfPlaceableArea puts one board height down — so a board only
  // nine tiles tall is looked for near the top of the world while a
  // twenty-four tile one is looked for far below. Rather than teach the
  // camera about two boards, this one is put where the camera already
  // looks: the table lands the same fraction of a board below the centre of
  // its own extent as the outdoor one does. Twelve by nine cannot sit
  // perfectly on the middle — nine is odd, so its centre falls on a half
  // tile — which leaves it a quarter of a cell to the right of the origin.
  return {
    placeableWidth: INTERIOR_BOARD.width,
    placeableHeight: INTERIOR_BOARD.depth,
    placeableX: 9,
    placeableY: 10,
  };
}
